import { useTableControls, TableColumn } from '../hooks/useTableControls'
import { User } from '../types'
import { ColumnVisibilityButton } from './ColumnVisibilityButton'
import { DatePicker } from './DatePicker'
import { TableToolbar } from './TableToolbar'
import {
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  Person as PersonIcon,
} from '@mui/icons-material'
import {
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material'
import React, { useState, useMemo } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'

interface UsersPreviewProps {
  readonly users: User[]
  readonly setUsers: React.Dispatch<React.SetStateAction<User[]>>
  readonly onPrimaryContactChange?: (email: string) => void
  readonly onUserAdded?: (user: User) => void
  readonly onUserUpdated?: (user: Partial<User>) => void
  readonly onUsersDeleted?: (userIds: string[]) => void
}

export function UsersPreview({
  users,
  setUsers,
  onPrimaryContactChange,
  onUserAdded,
  onUserUpdated,
  onUsersDeleted,
}: UsersPreviewProps) {
  const intl = useIntl()
  const [selectedUsers, setSelectedUsers] = useState<string[]>([])
  const [isAddUserModalOpen, setIsAddUserModalOpen] = useState(false)
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [editingUserId, setEditingUserId] = useState<string | null>(null)
  const [formFirstName, setFormFirstName] = useState('')
  const [formLastName, setFormLastName] = useState('')
  const [formEmail, setFormEmail] = useState('')
  const [formTitle, setFormTitle] = useState('')
  const [formPhone, setFormPhone] = useState('')
  const [formRole, setFormRole] = useState<User['role']>('User')
  const [formIsPrimaryContact, setFormIsPrimaryContact] = useState(false)
  const [wasPrimaryContact, setWasPrimaryContact] = useState(false)
  const [formDateAssigned, setFormDateAssigned] = useState(
    new Date().toISOString().split('T')[0]
  )

  const defaultColumns: TableColumn[] = useMemo(
    () => [
      {
        id: 'firstName',
        label: intl.formatMessage({
          id: 'organizations.users.table.firstName',
        }),
        visible: true,
        sortable: true,
      },
      {
        id: 'lastName',
        label: intl.formatMessage({ id: 'organizations.users.table.lastName' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'email',
        label: intl.formatMessage({ id: 'organizations.users.table.email' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'title',
        label: intl.formatMessage({ id: 'organizations.users.table.title' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'phone',
        label: intl.formatMessage({ id: 'organizations.users.table.phone' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'role',
        label: intl.formatMessage({ id: 'organizations.users.table.role' }),
        visible: true,
        sortable: true,
      },
      {
        id: 'dateAssigned',
        label: intl.formatMessage({
          id: 'organizations.users.table.dateAssigned',
        }),
        visible: true,
        sortable: true,
      },
    ],
    [intl]
  )

  const sortedUsers = useMemo(
    () =>
      [...users].sort((a, b) => {
        if (a.isPrimaryContact && !b.isPrimaryContact) return -1
        if (!a.isPrimaryContact && b.isPrimaryContact) return 1
        if (a.dateAssigned && b.dateAssigned) {
          return (
            new Date(b.dateAssigned).getTime() -
            new Date(a.dateAssigned).getTime()
          )
        }
        if (a.dateAssigned && !b.dateAssigned) return -1
        if (!a.dateAssigned && b.dateAssigned) return 1
        return 0
      }),
    [users]
  )

  const {
    page,
    rowsPerPage,
    columns,
    visibleColumns,
    paginatedData,
    sortBy,
    sortDirection,
    handleChangePage,
    handleChangeRowsPerPage,
    toggleColumn,
    handleSort,
  } = useTableControls(sortedUsers, defaultColumns)

  const handleToggleUser = (userId: string) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    )
  }

  const handleDeleteSelected = () => {
    if (selectedUsers.length > 0) {
      if (onUsersDeleted) {
        onUsersDeleted(selectedUsers)
      }
      setUsers((prev) =>
        prev.filter((user) => !selectedUsers.includes(user.id))
      )
      setSelectedUsers([])
    }
  }

  const handleOpenAddUserModal = () => {
    setFormFirstName('')
    setFormLastName('')
    setFormEmail('')
    setFormTitle('')
    setFormPhone('')
    setFormRole('User')
    setFormIsPrimaryContact(false)
    setFormDateAssigned(new Date().toISOString().split('T')[0])
    setIsAddUserModalOpen(true)
  }

  const handleCloseAddUserModal = () => setIsAddUserModalOpen(false)

  const handleAddUser = () => {
    if (
      formFirstName.trim() === '' ||
      formLastName.trim() === '' ||
      formEmail.trim() === ''
    )
      return

    const newUser: User = {
      id: Date.now().toString(),
      firstName: formFirstName.trim(),
      lastName: formLastName.trim(),
      email: formEmail.trim(),
      title: formTitle.trim(),
      phone: formPhone.trim(),
      role: formRole,
      isPrimaryContact: formIsPrimaryContact,
      dateAssigned: formDateAssigned,
    }

    setUsers((prev) => {
      if (formIsPrimaryContact) {
        return [
          ...prev.map((u) => ({ ...u, isPrimaryContact: false })),
          newUser,
        ]
      }
      return [...prev, newUser]
    })

    if (formIsPrimaryContact && onPrimaryContactChange) {
      onPrimaryContactChange(formEmail.trim())
    }
    if (onUserAdded) {
      onUserAdded(newUser)
    }
    handleCloseAddUserModal()
  }

  const handleOpenEditUserModal = (user: User) => {
    setEditingUserId(user.id)
    setFormFirstName(user.firstName)
    setFormLastName(user.lastName)
    setFormEmail(user.email)
    setFormTitle(user.title)
    setFormPhone(user.phone)
    setFormRole(user.role)
    setFormIsPrimaryContact(user.isPrimaryContact)
    setWasPrimaryContact(user.isPrimaryContact)
    setFormDateAssigned(user.dateAssigned)
    setIsEditUserModalOpen(true)
  }

  const handleCloseEditUserModal = () => {
    setIsEditUserModalOpen(false)
    setEditingUserId(null)
    setWasPrimaryContact(false)
  }

  const handleUpdateUser = () => {
    if (
      formFirstName.trim() === '' ||
      formLastName.trim() === '' ||
      formEmail.trim() === '' ||
      !editingUserId
    )
      return

    setUsers((prev) => {
      if (formIsPrimaryContact && !wasPrimaryContact) {
        return prev.map((u) =>
          u.id === editingUserId
            ? {
                ...u,
                firstName: formFirstName.trim(),
                lastName: formLastName.trim(),
                email: formEmail.trim(),
                title: formTitle.trim(),
                phone: formPhone.trim(),
                role: formRole,
                isPrimaryContact: true,
                dateAssigned: formDateAssigned,
              }
            : { ...u, isPrimaryContact: false }
        )
      }
      return prev.map((u) =>
        u.id === editingUserId
          ? {
              ...u,
              firstName: formFirstName.trim(),
              lastName: formLastName.trim(),
              email: formEmail.trim(),
              title: formTitle.trim(),
              phone: formPhone.trim(),
              role: formRole,
              isPrimaryContact: formIsPrimaryContact,
              dateAssigned: formDateAssigned,
            }
          : u
      )
    })

    if (formIsPrimaryContact && !wasPrimaryContact && onPrimaryContactChange) {
      onPrimaryContactChange(formEmail.trim())
    }
    if (onUserUpdated) {
      onUserUpdated({
        id: editingUserId,
        firstName: formFirstName.trim(),
        lastName: formLastName.trim(),
        email: formEmail.trim(),
        title: formTitle.trim(),
        phone: formPhone.trim(),
        role: formRole,
      })
    }
    handleCloseEditUserModal()
  }

  const getRoleColor = (role: string): 'default' | 'info' | 'primary' => {
    switch (role) {
      case 'Primary Contact':
        return 'primary'
      case 'User':
        return 'info'
      default:
        return 'default'
    }
  }

  const renderUserFormDialog = (isEdit: boolean) => (
    <Dialog
      open={isEdit ? isEditUserModalOpen : isAddUserModalOpen}
      onClose={isEdit ? handleCloseEditUserModal : handleCloseAddUserModal}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { bgcolor: '#31353D', backgroundImage: 'none' } }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          {isEdit
            ? intl.formatMessage({ id: 'organizations.users.editUser' })
            : intl.formatMessage({ id: 'organizations.users.addUser' })}
          <IconButton
            onClick={
              isEdit ? handleCloseEditUserModal : handleCloseAddUserModal
            }
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label={intl.formatMessage({
                id: 'organizations.users.form.firstName',
              })}
              value={formFirstName}
              onChange={(e) => setFormFirstName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'organizations.users.form.firstNamePlaceholder',
              })}
              fullWidth
            />
            <TextField
              label={intl.formatMessage({
                id: 'organizations.users.form.lastName',
              })}
              value={formLastName}
              onChange={(e) => setFormLastName(e.target.value)}
              placeholder={intl.formatMessage({
                id: 'organizations.users.form.lastNamePlaceholder',
              })}
              fullWidth
            />
          </Box>
          <TextField
            label={intl.formatMessage({ id: 'organizations.users.form.title' })}
            value={formTitle}
            onChange={(e) => setFormTitle(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'organizations.users.form.titlePlaceholder',
            })}
            fullWidth
          />
          <TextField
            label={intl.formatMessage({ id: 'organizations.users.form.email' })}
            type="email"
            value={formEmail}
            onChange={(e) => setFormEmail(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'organizations.users.form.emailPlaceholder',
            })}
            fullWidth
          />
          <TextField
            label={intl.formatMessage({ id: 'organizations.users.form.phone' })}
            value={formPhone}
            onChange={(e) => setFormPhone(e.target.value)}
            placeholder={intl.formatMessage({
              id: 'organizations.users.form.phonePlaceholder',
            })}
            fullWidth
          />
          <DatePicker
            value={formDateAssigned}
            onChange={setFormDateAssigned}
            placeholder={intl.formatMessage({
              id: 'organizations.users.form.dateAssignedPlaceholder',
            })}
            label={intl.formatMessage({
              id: 'organizations.users.table.dateAssigned',
            })}
            disabled
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formIsPrimaryContact}
                onChange={(e) => setFormIsPrimaryContact(e.target.checked)}
                disabled={isEdit ? wasPrimaryContact : false}
              />
            }
            label={intl.formatMessage({
              id: 'organizations.users.form.setPrimaryContact',
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={isEdit ? handleCloseEditUserModal : handleCloseAddUserModal}
        >
          <FormattedMessage id="organizations.users.form.cancel" />
        </Button>
        <Button
          variant="contained"
          onClick={isEdit ? handleUpdateUser : handleAddUser}
          disabled={
            formFirstName.trim() === '' ||
            formLastName.trim() === '' ||
            formEmail.trim() === '' ||
            formTitle.trim() === '' ||
            formPhone.trim() === ''
          }
        >
          {isEdit
            ? intl.formatMessage({ id: 'organizations.users.form.saveChanges' })
            : intl.formatMessage({ id: 'organizations.users.addUser' })}
        </Button>
      </DialogActions>
    </Dialog>
  )

  return (
    <Card>
      <Box
        sx={{
          p: 3,
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6">
            <FormattedMessage id="organizations.users.title" />
          </Typography>
          {selectedUsers.length > 0 && (
            <Button
              color="error"
              variant="outlined"
              size="small"
              startIcon={<DeleteIcon />}
              onClick={handleDeleteSelected}
            >
              {intl.formatMessage(
                { id: 'organizations.users.delete' },
                { count: selectedUsers.length }
              )}
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {users.length > 0 && (
            <ColumnVisibilityButton
              columns={columns}
              onToggleColumn={toggleColumn}
            />
          )}
          <Button
            variant="contained"
            size="medium"
            startIcon={<PersonAddIcon />}
            onClick={handleOpenAddUserModal}
          >
            <FormattedMessage id="organizations.users.addUser" />
          </Button>
        </Box>
      </Box>

      {users.length === 0 ? (
        <Box sx={{ p: 12, textAlign: 'center' }}>
          <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            <FormattedMessage id="organizations.users.empty" />
          </Typography>
          <Typography variant="body2" color="text.disabled">
            <FormattedMessage id="organizations.users.emptyHint" />
          </Typography>
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  {visibleColumns.map((column) => (
                    <TableCell key={column.id}>
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={sortBy === column.id}
                          direction={
                            sortBy === column.id ? sortDirection : 'asc'
                          }
                          onClick={() => handleSort(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.map((user) => (
                  <TableRow
                    key={user.id}
                    hover
                    onClick={() => handleOpenEditUserModal(user)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell
                      padding="checkbox"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Checkbox
                        checked={selectedUsers.includes(user.id)}
                        onChange={() => handleToggleUser(user.id)}
                      />
                    </TableCell>
                    {visibleColumns.map((column) => {
                      if (column.id === 'firstName') {
                        return (
                          <TableCell key={column.id}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Typography variant="body1">
                                {user.firstName}
                              </Typography>
                              {user.isPrimaryContact ? (
                                <Chip
                                  label="Primary"
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              ) : null}
                            </Box>
                          </TableCell>
                        )
                      }
                      if (column.id === 'lastName')
                        return (
                          <TableCell key={column.id}>
                            <Typography variant="body1">
                              {user.lastName}
                            </Typography>
                          </TableCell>
                        )
                      if (column.id === 'email') {
                        return (
                          <TableCell key={column.id}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <EmailIcon fontSize="small" color="action" />
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {user.email}
                              </Typography>
                            </Box>
                          </TableCell>
                        )
                      }
                      if (column.id === 'title')
                        return (
                          <TableCell key={column.id}>
                            <Typography variant="body2" color="text.secondary">
                              {user.title}
                            </Typography>
                          </TableCell>
                        )
                      if (column.id === 'phone')
                        return (
                          <TableCell key={column.id}>
                            <Typography variant="body2" color="text.secondary">
                              {user.phone}
                            </Typography>
                          </TableCell>
                        )
                      if (column.id === 'role')
                        return (
                          <TableCell key={column.id}>
                            <Chip
                              label={
                                user.isPrimaryContact
                                  ? 'Primary Contact'
                                  : 'User'
                              }
                              size="small"
                              color={getRoleColor(
                                user.isPrimaryContact
                                  ? 'Primary Contact'
                                  : 'User'
                              )}
                            />
                          </TableCell>
                        )
                      if (column.id === 'dateAssigned') {
                        return (
                          <TableCell key={column.id}>
                            <Typography variant="body2" color="text.secondary">
                              {user.dateAssigned
                                ? new Date(
                                    user.dateAssigned
                                  ).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })
                                : '-'}
                            </Typography>
                          </TableCell>
                        )
                      }
                      return null
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TableToolbar
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={users.length}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </>
      )}

      {renderUserFormDialog(false)}
      {renderUserFormDialog(true)}
    </Card>
  )
}
